import { LoaderFunctionArgs, json, type MetaFunction } from "@remix-run/node";
import { createPricingMeta } from "~/lib/meta";
import { useLoaderData, Link, useFetcher } from "@remix-run/react";
import { useState } from "react";
import { eq } from "drizzle-orm";
import { db } from "~/database/db";
import { userTable } from "~/database/schema";
import { getCookieAuth } from "~/lib/.server/cookie";
import { getUserSubscription } from "~/database/queries";
import { Layout } from "~/components/Layout";
import { Container } from "~/components/Container";
import { SUBSCRIPTION } from "~/lib/constants";
import { canAccessPaidPlanFeatures, isPaidPlanPubliclyAvailable } from "~/lib/.server/paid-plan-access";
import { FreePlanCard } from "~/components/FreePlanCard";
import { DeepDivePricingCard } from "~/components/DeepDivePricingCard";

export const meta: MetaFunction = () => createPricingMeta();

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const auth = await getCookieAuth(request);

  let user = null;
  let subscription = null;

  if (auth) {
    user = await db.query.userTable.findFirst({
      where: eq(userTable.id, auth.userId),
    });

    if (user) {
      subscription = await getUserSubscription(user.id);
    }
  }

  // 有料プラン機能へのアクセス権チェック
  const canAccessPaidPlan = auth ? canAccessPaidPlanFeatures(auth.userId) : false;
  const isPaidPlanPublic = isPaidPlanPubliclyAvailable();

  return json({
    user,
    subscription,
    isActiveSubscriber: subscription?.status === SUBSCRIPTION.STATUS.ACTIVE,
    canAccessPaidPlan,
    isPaidPlanPublic,
  });
};

// Checkout APIレスポンスの型定義
type CheckoutResponse = {
  checkoutUrl?: string;
  sessionId?: string;
  error?: string;
};

export default function Plan() {
  const { user, isActiveSubscriber, canAccessPaidPlan, isPaidPlanPublic } = useLoaderData<typeof loader>();
  const fetcher = useFetcher<CheckoutResponse>();
  const [billingInterval, setBillingInterval] = useState<"month" | "year">("year");

  const handleSubscribe = () => {
    fetcher.submit({ billingInterval }, {
      method: "post",
      action: "/api/subscription/create-checkout"
    });
  };

  // Stripe Checkout URLが返ってきたらリダイレクト
  if (fetcher.data?.checkoutUrl) {
    window.location.href = fetcher.data.checkoutUrl;
  }

  return (
    <Layout user={user}>
      <Container>
        <div className="py-16">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start with our free plan or unlock advanced features with Deep Dive.
              Cancel anytime, no questions asked.
            </p>
          </div>

          {/* Error Message */}
          {fetcher.data?.error && (
            <div className="max-w-4xl mx-auto mb-6">
              <div className="bg-red-50 text-red-600 p-4 rounded-lg">
                {fetcher.data.error}
              </div>
            </div>
          )}

          {/* Plan Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto pt-6">
            {/* Free Plan */}
            <FreePlanCard
              borderClassName={!isActiveSubscriber ? 'border-blue-500' : 'border-gray-300'}
              badge={
                !user ? (
                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                    Default
                  </span>
                ) : user && !isActiveSubscriber ? (
                  <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded">
                    Current Plan
                  </span>
                ) : undefined
              }
              action={
                !user ? (
                  <Link to="/signin" className="block">
                    <button className="w-full bg-gray-800 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                      Get Started for Free
                    </button>
                  </Link>
                ) : !isActiveSubscriber ? (
                  <button className="w-full bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg cursor-not-allowed" disabled>
                    Current Plan
                  </button>
                ) : (
                  <button className="w-full bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg cursor-not-allowed" disabled>
                    Available Anytime
                  </button>
                )
              }
            />

            {/* Deep Dive */}
            <DeepDivePricingCard
              showPaidPlan={canAccessPaidPlan || isPaidPlanPublic}
              billingInterval={billingInterval}
              onBillingIntervalChange={setBillingInterval}
              showCurrentPlanBadge={isActiveSubscriber}
              borderClassName={isActiveSubscriber ? 'border-purple-500' : isPaidPlanPublic ? 'border-gray-300' : 'border-gray-200'}
              action={
                !user ? (
                  isPaidPlanPublic ? (
                    <Link to="/signin" className="block">
                      <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                        Sign In to Subscribe
                      </button>
                    </Link>
                  ) : (
                    <button className="w-full bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg cursor-not-allowed" disabled>
                      Coming Soon
                    </button>
                  )
                ) : isActiveSubscriber ? (
                  <Link to="/settings" className="block">
                    <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200">
                      Manage Subscription
                    </button>
                  </Link>
                ) : canAccessPaidPlan ? (
                  <button
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200"
                    onClick={handleSubscribe}
                    disabled={fetcher.state === "submitting"}
                  >
                    {fetcher.state === "submitting" ? "Processing..." : "Subscribe Now"}
                  </button>
                ) : (
                  <button className="w-full bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg cursor-not-allowed" disabled>
                    {isPaidPlanPublic ? "Subscribe Now" : "Coming Soon"}
                  </button>
                )
              }
            />
          </div>

          {/* Feature Comparison Section */}
          <div className="mt-20 max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">Detailed Feature Comparison</h2>
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Free</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Deep Dive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Raindrops per Dew</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">20</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">100</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">Dew History</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Latest {SUBSCRIPTION.PLANS.FREE.MAX_DEWS_HISTORY}</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Latest {SUBSCRIPTION.PLANS.PAID.MAX_DEWS_HISTORY}</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Collection Exclusion</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Up to 1</td>
                    <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">Unlimited</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900">Email Notifications</td>
                    <td className="px-6 py-4 text-center text-sm text-green-600 font-semibold">✓</td>
                    <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 text-sm text-gray-900">Notification Time</td>
                    <td className="px-6 py-4 text-center text-sm text-gray-600">Standard</td>
                    <td className="px-6 py-4 text-center text-sm text-purple-600 font-semibold">Customizable</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* FAQ Link */}
          {/* Deep Dive プラン解放後にアンコメントする予定。 */}
          {/* <div className="mt-16 text-center">
            <p className="text-gray-600 mb-4">Have questions about our plans?</p>
            <Link to="/#faq" className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
              Check out our FAQ
            </Link>
          </div> */}
        </div>
      </Container>
    </Layout>
  );
}
